import { pick } from 'ramda';
import { batch } from 'react-redux';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { Form } from 'js/components/common/Form';
import { Button } from 'js/components/common/Button';
import { useComponentConfig } from 'js/hooks/component';
import React, { useMemo, useCallback, useState } from 'react';
import { Radio, InputGroup, Checkbox } from 'js/components/common/FormFields';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import * as helpers from './helpers';
import * as constants from '../../../../constants';
import { useGetFilterOptions } from '../../../../hooks';
import { IGamesGridConfig } from '../../../../../../types';
import * as actions from '../../../../../../store/actions';
import { DEFAULT_FILTERS_STATE } from '../../../../../../store/constants';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../../../../../../store';

import style from './Content.scss';

export const Content: React.FC = () => {
    const {
        isSortingEnabled,
        isLabelFilterEnabled,
        isProviderFilterEnabled,
        isCategoryFilterEnabled,
        isJackpotSortingEnabled
    } = useComponentConfig<IGamesGridConfig>();
    const translate = useTranslations();
    const dispatch = useDynamicReducerDispatch();
    const state = useDynamicReducerSelector(pick(['filterSortBy', 'filterProviders', 'filterLabels', 'filterCategory', 'filterCustomFields']));
    const [formInitialValues, setFormInitialValues] = useState({
        filterSortBy: state.filterSortBy,
        filterLabels: state.filterLabels,
        filterCategory: state.filterCategory,
        filterProviders: state.filterProviders,
        filterCustomFields: state.filterCustomFields
    });
    const sortByOptions = isJackpotSortingEnabled ? constants.SORT_BY_OPTIONS_WITH_JACKPOTS : constants.SORT_BY_OPTIONS;

    const handleFormSubmit = useCallback((values: { [key: string]: string }): void => {
        const storeData = helpers.convertFormValuesToStoreData(values);

        batch(() => {
            dispatch(actions.updateAllFilters(storeData));
            dispatch(actions.toggleFilterPopup(false));
        });
    }, [dispatch]);

    const handlePopupClose = useCallback(() => {
        dispatch(actions.toggleFilterPopup(false));
    }, [dispatch]);

    const handleClearClick = useCallback(() => {
        // dirty hack to reset form values
        setFormInitialValues({} as any);
        setTimeout(() => {
            setFormInitialValues(DEFAULT_FILTERS_STATE);
        }, 0);
    }, [setFormInitialValues]);

    const filters = useGetFilterOptions();

    const initialValues = useMemo(() => {
        return helpers.convertStoreDataToFormValues(formInitialValues);
    }, [formInitialValues]);

    return (
        <>
            <div className={style.header}>
                <AccessibilityElement onClick={handleClearClick}>
                    <span className={style.clearLink}>
                        {translate('playtech.mobile.system.games-grid.filters.clearFilters')}
                    </span>
                </AccessibilityElement>
                <Icon
                    type={'closeDark'}
                    className={style.closeIcon}
                    onClick={handlePopupClose}
                    aria-label={translate('playtech.mobile.system.games-grid.filters.closeAriaLabel')}
                />
            </div>
            <div className={style.content}>
                <Form
                    onSubmit={handleFormSubmit}
                    initialValues={initialValues}
                    render={({ submitting, handleSubmit }) => (
                        <>
                            <div>
                                {isSortingEnabled && (
                                    <InputGroup
                                        label={translate('playtech.mobile.system.games-grid.filters.sortBy.label')}
                                        classNames={{
                                            inputGroup: style.inputGroup,
                                            inputGroupLabel: style.inputGroupLabel
                                        }}
                                    >
                                        <>
                                            {sortByOptions.map((option) => (
                                                <Radio
                                                    value={option.value}
                                                    key={option.value}
                                                    label={translate(option.label)}
                                                    name="games-grid-filters-sort-by"
                                                    aria-label={translate(option.label)}
                                                    id={`games-grid-filters-sort-by-${option.value}`}
                                                />
                                            ))}
                                        </>
                                    </InputGroup>
                                )}
                                {isProviderFilterEnabled && Boolean(filters.providerOptions.length) && (
                                    <InputGroup
                                        label={translate('playtech.mobile.system.games-grid.filters.provider.label')}
                                        classNames={{
                                            inputGroup: style.inputGroup,
                                            inputGroupLabel: style.inputGroupLabel
                                        }}
                                    >
                                        <>
                                            {filters.providerOptions.map((option) => (
                                                <Checkbox
                                                    key={option.value}
                                                    label={option.label}
                                                    aria-label={option.label}
                                                    id={`games-grid-filters-provider-${option.value}`}
                                                    name={`games-grid-filters-provider-${option.value}`}
                                                />
                                            ))}
                                        </>
                                    </InputGroup>
                                )}
                                {isCategoryFilterEnabled && Boolean(filters.categoryOptions.length) && (
                                    <InputGroup
                                        label={translate('playtech.mobile.system.games-grid.filters.category.label')}
                                        classNames={{
                                            inputGroup: style.inputGroup,
                                            inputGroupLabel: style.inputGroupLabel
                                        }}
                                    >
                                        <>
                                            {filters.categoryOptions.map((option) => (
                                                <Checkbox
                                                    key={option.value}
                                                    label={option.label}
                                                    aria-label={option.label}
                                                    id={`games-grid-filters-category-${option.value}`}
                                                    name={`games-grid-filters-category-${option.value}`}
                                                />
                                            ))}
                                        </>
                                    </InputGroup>
                                )}
                                {isLabelFilterEnabled && Boolean(filters.labelOptions.length) && (
                                    <InputGroup
                                        label={translate('playtech.mobile.system.games-grid.filters.label.label')}
                                        classNames={{
                                            inputGroup: style.inputGroup,
                                            inputGroupLabel: style.inputGroupLabel
                                        }}
                                    >
                                        <>
                                            {filters.labelOptions.map((option) => (
                                                <Checkbox
                                                    key={option.value}
                                                    label={option.label}
                                                    aria-label={option.label}
                                                    id={`games-grid-filters-label-${option.value}`}
                                                    name={`games-grid-filters-label-${option.value}`}
                                                />
                                            ))}
                                        </>
                                    </InputGroup>
                                )}
                                {Boolean(filters?.customFieldsOptions?.length)
                                    && filters.customFieldsOptions.map((customField) => (
                                    <InputGroup
                                        key={customField.name}
                                        label={translate(`playtech.mobile.system.games-grid.filters.customField.${customField.name}.label`, customField.name)}
                                        classNames={{
                                            inputGroup: style.inputGroup,
                                            inputGroupLabel: style.inputGroupLabel
                                        }}
                                    >
                                        <>
                                            {customField.options.map((option) => (
                                                <Checkbox
                                                    key={option.value}
                                                    label={option.label}
                                                    aria-label={option.label}
                                                    id={`games-grid-filters-customField-${customField.name}-${option.value}`}
                                                    name={`games-grid-filters-customField-${customField.name}-${option.value}`}
                                                />
                                            ))}
                                        </>
                                    </InputGroup>
                                ))}
                            </div>
                            <div className={style.submitContainer}>
                                <Button
                                    isNew
                                    size="large"
                                    disabled={submitting}
                                    onClick={handleSubmit}
                                    className={style.submitButton}
                                >
                                    {translate('playtech.mobile.system.games-grid.filters.submit')}
                                </Button>
                            </div>
                        </>
                    )}>
                </Form>
            </div>
        </>
    );
};
